import {
  type Audience,
  EVENT_KINDS,
  type EventKind,
  type SchoolEvent,
  addDays,
  shortDate,
  todayIso,
  useClasses,
  useCurrentContext,
  useEventActions,
  useEvents,
} from '@shkola/api';
import { MapPinIcon } from '@phosphor-icons/react/MapPin';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ErrorText, Field, Modal, Select, Spinner } from '../components/ui';
import {
  Block,
  EventArt,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PageHead,
  PillIcon,
  grad,
  pillClass,
  ringSmall,
} from '../design/forms';
import { PlusIcon, XIcon } from '../design/icons';
import { dayMonthShort, monthName, weekdayShort } from '../student/home/model';
import { EVENT_GRAD } from '../student/home/ShapesDay';

/*
 * «Події» in «Форми 2» (28.10): the title on the paper; the nearest three as soft cards in their kind's
 * colour with its picture big and quiet behind and how soon in words (as «У школі» on the home); then one
 * white block with the rest by month — the day as a small leaf, the kind's small picture, the title, when
 * and where. The homeroom teacher and the administration add an event and remove their own.
 */

/** «пн, 19 жовт.», «19–23 жовт.», with «сьогодні / завтра». */
export function useEventWhen() {
  const { t } = useTranslation();
  const today = todayIso();
  return (e: SchoolEvent) => {
    const day =
      e.startsOn === today
        ? t('events.today')
        : e.startsOn === addDays(today, 1)
          ? t('events.tomorrow')
          : shortDate(e.startsOn);
    const range = e.endsOn > e.startsOn ? `${day} – ${shortDate(e.endsOn)}` : day;
    return e.atTime ? `${range}, ${e.atTime}` : range;
  };
}

/** «сьогодні», «завтра», «через 4 дні», «триває до 30 жовт.» */
function useUntil() {
  const { t } = useTranslation();
  const today = todayIso();
  return (e: SchoolEvent) => {
    if (e.startsOn < today)
      return t('studentHome.glass.school.goesOn', { date: dayMonthShort(e.endsOn) });
    const days = Math.round((Date.parse(e.startsOn) - Date.parse(today)) / 86_400_000);
    return days === 0
      ? t('studentHome.glass.school.today')
      : days === 1
        ? t('studentHome.glass.school.tomorrow')
        : t('studentHome.glass.school.in', { count: days });
  };
}

/** A label: a soft rectangle in a gradient (white on a coloured card), never a pill. */
function Tag({ tone, white, children }: { tone: Grad; white?: boolean; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 max-w-full shrink-0 items-center gap-1 truncate rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={
        white
          ? { backgroundColor: 'rgb(255 255 255 / 0.75)', color: GRAD[tone].ink }
          : { backgroundImage: grad(tone), color: GRAD[tone].ink }
      }
    >
      {children}
    </span>
  );
}

function NewEvent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const classes = useClasses(ctx.schoolId);
  const actions = useEventActions(ctx.schoolId);
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const audiences: Audience[] =
    ctx.kind === 'admin'
      ? ['school', 'parents', 'staff', 'class', 'class_parents']
      : ['class', 'class_parents'];
  const [audience, setAudience] = useState<Audience>(audiences[0] as Audience);
  const [classId, setClassId] = useState(homeroom?.id ?? '');
  const [kind, setKind] = useState<EventKind>('celebration');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsOn, setStartsOn] = useState(addDays(todayIso(), 1));
  const [endsOn, setEndsOn] = useState('');
  const [atTime, setAtTime] = useState('');
  const [place, setPlace] = useState('');
  const [error, setError] = useState<string>();
  const needsClass = audience === 'class' || audience === 'class_parents';
  const klass = homeroom?.id ?? (classId || classes.data?.[0]?.id);

  return (
    <Modal title={t('events.new')} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(undefined);
          actions.create
            .mutateAsync({
              audience,
              classId: needsClass ? (klass ?? null) : null,
              kind,
              title,
              description,
              startsOn,
              endsOn: endsOn || startsOn,
              atTime,
              place,
            })
            .then(onClose, () => setError(t('common.error')));
        }}
      >
        <div className="flex flex-wrap gap-2">
          <Select
            label={t('events.audience')}
            value={audience}
            onChange={(e) => setAudience(e.target.value as Audience)}
          >
            {audiences.map((a) => (
              <option key={a} value={a}>
                {t(`announcements.audiences.${a}`)}
              </option>
            ))}
          </Select>
          {needsClass && !homeroom && (
            <Select
              aria-label={t('events.audience')}
              value={klass ?? ''}
              onChange={(e) => setClassId(e.target.value)}
            >
              {(classes.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
          <Select
            label={t('events.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as EventKind)}
          >
            {EVENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`events.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </div>
        <Field
          id="event-title"
          label={t('events.titleLabel')}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Field
            id="event-from"
            type="date"
            label={t('events.from')}
            required
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
          <Field
            id="event-to"
            type="date"
            label={t('events.to')}
            min={startsOn}
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
          <Field
            id="event-time"
            type="time"
            label={t('events.time')}
            value={atTime}
            onChange={(e) => setAtTime(e.target.value)}
          />
        </div>
        <Field
          id="event-place"
          label={t('events.place')}
          maxLength={200}
          value={place}
          onChange={(e) => setPlace(e.target.value)}
        />
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('events.description')}
          </span>
          <textarea
            className="w-full rounded-[14px] bg-white p-3.5 text-[15px] leading-6 outline-none ring-1 ring-[#E4E0D7] ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]"
            style={{ color: INK }}
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={actions.create.isPending || !title.trim()}>
          {t('events.new')}
        </Button>
      </form>
    </Modal>
  );
}

export function EventsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const today = todayIso();
  const events = useEvents(ctx.schoolId, today);
  const classes = useClasses(ctx.schoolId);
  const actions = useEventActions(ctx.schoolId);
  const when = useEventWhen();
  const until = useUntil();
  const [creating, setCreating] = useState(false);
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const canCreate = ctx.kind === 'admin' || homeroom !== undefined;
  const canRemove = (e: SchoolEvent) =>
    ctx.kind === 'admin' || (homeroom !== undefined && e.classId === homeroom.id);
  const staff = ctx.kind === 'admin' || ctx.kind === 'teacher';
  const className = (id: string | null) => classes.data?.find((c) => c.id === id)?.label ?? '';
  const all = events.data ?? [];
  const near = all.slice(0, 3);
  const later = all.slice(3);

  const months = new Map<string, SchoolEvent[]>();
  for (const e of later) {
    const key = (e.startsOn < today ? today : e.startsOn).slice(0, 7);
    months.set(key, [...(months.get(key) ?? []), e]);
  }

  /** Who it is for, when it is not the whole school: «Класу та батькам · 7-Б». */
  const audience = (e: SchoolEvent) =>
    (staff || e.classId) && e.audience !== 'school'
      ? `${t(`announcements.audiences.${e.audience}`)}${e.classId ? ` · ${className(e.classId)}` : ''}`
      : undefined;

  const remove = (e: SchoolEvent) =>
    canRemove(e) && (
      <button
        type="button"
        aria-label={`${t('events.remove')}: ${e.title}`}
        title={t('events.remove')}
        disabled={actions.remove.isPending}
        className={`${ringSmall} disabled:opacity-40`}
        onClick={() => actions.remove.mutate(e.id)}
      >
        <XIcon size={13} weight="bold" aria-hidden />
      </button>
    );

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('events.title')}>
        {canCreate && (
          <button
            type="button"
            className={`${pillClass('dark')} max-lg:w-11 max-lg:justify-center max-lg:px-0`}
            onClick={() => setCreating(true)}
          >
            <span className="max-lg:sr-only">{t('events.new')}</span>
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      {events.isPending ? (
        <Block>
          <Spinner />
        </Block>
      ) : all.length === 0 ? (
        <Block className="items-center py-14 text-center">
          <EventArt kind="holiday" tone="mint" size={96} />
          <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
            {t('events.empty')}
          </p>
        </Block>
      ) : (
        <>
          {/* the nearest: soft cards in the kind's colour, the picture big and quiet behind */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {near.map((e) => {
              const tone = EVENT_GRAD[e.kind];
              const who = audience(e);
              return (
                <article
                  key={e.id}
                  className="relative isolate flex min-h-[220px] min-w-0 flex-col overflow-hidden rounded-[28px] p-5"
                  style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
                >
                  <EventArt
                    kind={e.kind}
                    tone={tone}
                    size={160}
                    ghost
                    className="pointer-events-none absolute -top-5 -right-6 -z-10"
                  />
                  <div className="flex items-start gap-2">
                    <span className="flex min-w-0 flex-wrap gap-1.5">
                      <Tag tone={tone} white>
                        {until(e)}
                      </Tag>
                      {who && (
                        <Tag tone={tone} white>
                          {who}
                        </Tag>
                      )}
                    </span>
                    <span className="ml-auto">{remove(e)}</span>
                  </div>
                  <h2
                    className="mt-auto pt-6 font-display text-[24px] leading-[30px] font-light tracking-[-0.025em]"
                    style={{ color: INK }}
                  >
                    {e.title}
                  </h2>
                  {e.description && (
                    <p
                      className="mt-1.5 line-clamp-3 text-[14px] leading-5"
                      style={{ color: INK }}
                      title={e.description}
                    >
                      {e.description}
                    </p>
                  )}
                  <p className="mt-3 text-[13px] leading-[18px]">
                    {[when(e), e.place].filter(Boolean).join(' · ')}
                  </p>
                </article>
              );
            })}
          </div>

          {/* the rest by month on one white block */}
          {months.size > 0 && (
            <Block>
              {[...months].map(([month, list], i) => (
                <section key={month} className={i > 0 ? 'mt-6' : ''}>
                  <h2
                    className="mb-1 flex items-baseline gap-2 font-display text-[24px] leading-[30px] font-light tracking-[-0.03em]"
                    style={{ color: INK }}
                  >
                    {monthName(`${month}-01`)} {month.slice(0, 4)}
                    <span
                      className="font-sans text-[13px] font-normal tracking-normal"
                      style={{ color: INK_FAINT }}
                    >
                      {list.length}
                    </span>
                  </h2>
                  <ul className="flex flex-col">
                    {list.map((e) => {
                      const tone = EVENT_GRAD[e.kind];
                      const who = audience(e);
                      const day = e.startsOn < today ? today : e.startsOn;
                      return (
                        <li
                          key={e.id}
                          className="flex items-start gap-3 border-t border-[#E4E0D7] py-4 first:border-t-0 sm:gap-4"
                        >
                          <span className="flex w-11 shrink-0 flex-col items-center pt-0.5 sm:w-12">
                            <span
                              className="font-display text-[28px] leading-8 font-light tracking-[-0.03em]"
                              style={{ color: INK }}
                            >
                              {Number(day.slice(8, 10))}
                            </span>
                            <span className="text-[12px]" style={{ color: INK_SOFT }}>
                              {weekdayShort(day)}
                            </span>
                          </span>
                          <EventArt kind={e.kind} tone={tone} size={44} className="mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[17px] leading-6 font-medium" style={{ color: INK }}>
                              {e.title}
                            </p>
                            <p className="text-[13.5px] leading-5" style={{ color: INK_SOFT }}>
                              {when(e)}
                            </p>
                            {e.description && (
                              <p
                                className="mt-1 max-w-[760px] text-[15px] leading-[22px]"
                                style={{ color: INK }}
                              >
                                {e.description}
                              </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <Tag tone={tone}>{t(`events.kinds.${e.kind}`)}</Tag>
                              {e.place && (
                                <Tag tone="stone">
                                  <MapPinIcon size={12} weight="fill" aria-hidden />
                                  {e.place}
                                </Tag>
                              )}
                              {who && <Tag tone="lavender">{who}</Tag>}
                            </div>
                          </div>
                          {remove(e)}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </Block>
          )}
        </>
      )}
      {creating && <NewEvent onClose={() => setCreating(false)} />}
    </div>
  );
}
